import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { avatarHue } from '@/lib/avatarHue';
import { useBlockedUsers, type BlockedUser } from '@/lib/useBlockedUsers';

/**
 * Blocked-users management list for the Privacy settings tab.
 *
 * Renders the people the current user has blocked with an Unblock action.
 * Avatar + name styling mirrors MemberPanel so blocked rows read the same
 * as everywhere else a user appears.
 */
export function BlockedUsersList() {
  const { t } = useTranslation();
  const { blocks, isLoading, isError, refetch, unblock, unblockingId } = useBlockedUsers();

  if (isLoading) {
    return <Note>{t('common.loading')}</Note>;
  }

  if (isError) {
    return (
      <Note>
        {t('prof.blocked.loadFailed')}{' '}
        <button
          type="button"
          onClick={refetch}
          className="font-medium text-accent underline-offset-2 hover:underline"
        >
          {t('prof.blocked.retry')}
        </button>
      </Note>
    );
  }

  if (blocks.length === 0) {
    return <Note>{t('prof.blocked.empty')}</Note>;
  }

  return (
    <ul className="flex flex-col gap-0.5">
      {blocks.map(({ user }) => (
        <li
          key={user.id}
          className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-white/[0.03]"
        >
          <BlockedAvatar user={user} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] text-ink">
              {user.displayName || user.username}
            </div>
            <div className="truncate text-[11px] text-ink-muted">@{user.username}</div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            loading={unblockingId === user.id}
            onClick={() => unblock(user.id)}
          >
            {t('prof.blocked.unblock')}
          </Button>
        </li>
      ))}
    </ul>
  );
}

function BlockedAvatar({ user }: { user: BlockedUser }) {
  const label = (user.displayName || user.username).charAt(0).toUpperCase();
  const hue = avatarHue(user.id);
  return (
    <div
      className={
        'grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full text-xs font-light tracking-[-0.015em] ' +
        (user.avatarUrl ? 'bg-white/6' : `rounded-full ${hue.bg} ${hue.text}`)
      }
    >
      {user.avatarUrl ? (
        <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        label
      )}
    </div>
  );
}

function Note({ children }: { children: ReactNode }) {
  return <p className="px-2 py-3 text-[12px] text-ink-muted">{children}</p>;
}
